import { flattenStyle, type StyleProp } from '@adsk/alloy-react-theme';

/**
 * Preprocesses component props by flattening the style prop for styled-components compatibility.
 *
 * ## What is this?
 * A utility function that normalizes complex style objects into a format that styled-components
 * can properly handle. It transforms nested, responsive, and theme-based style values into
 * standard CSS objects.
 *
 * @template T - Component props type that extends an object with optional style property
 * @param props - The component props object
 * @param props.style - Optional style object that may contain complex nested values
 *
 * @returns The same props object with normalized style prop (mutated in place)
 *
 * @example
 * ```tsx
 * // Setup with styled-components
 * const Box = styled.div.attrs(preprocessStyleProp)<BoxProps>`
 *   ${base}
 * `;
 *
 * // Before preprocessing (what you pass to component)
 * const complexStyle = [
 *  { margin: '8px' },
 *  { color: 'primary', fontSize: 'md' },
 * ]; // ❌ Browser can't understand arrays
 *
 * // After preprocessing (what styled-components receives)
 * const flattenedStyle = {
 *   margin: '8px',
 *   color: '#3182ce', // Resolved theme color
 *   fontSize: '16px', // Resolved theme font size
 * }; // ✅ Browser can understand this object
 * ```
 * @see {@link flattenStyle} - Core function that performs the style flattening
 * @see {@link StyleProp} - Type definition for acceptable style values
 */
export const preprocessStyleProp = <T extends { style?: StyleProp }>(
  props: T,
): T => {
  if (props?.style) {
    props.style = flattenStyle(props.style);
  }
  return props;
};
